import React, { useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { connectBoard, mondayGql } from '../actions/mondayActions';
import { FiUpload, FiLink, FiColumns, FiZap, FiPlus, FiTrash2, FiEye, FiEyeOff, FiCheck, FiX, FiAlertTriangle, FiDownload, FiArrowRight, FiArrowLeft, FiRefreshCw } from 'react-icons/fi';

// ── CSV parsing ──
function parseCSVLine(line, delim) {
  if (delim === '\t') return line.split('\t').map(v => v.trim().replace(/^"|"$/g, ''));
  const res = [];
  let inQ = false, cur = '';
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else inQ = !inQ;
    } else if (c === ',' && !inQ) {
      res.push(cur.trim()); cur = '';
    } else cur += c;
  }
  res.push(cur.trim());
  return res;
}

function parseCSV(text) {
  const raw = text.trim().split(/\r?\n/);
  if (!raw.length) return { headers: [], rows: [] };
  const delim = raw[0].includes('\t') ? '\t' : ',';
  const parsed = raw.map(l => parseCSVLine(l, delim));
  const first = parsed.findIndex(r => r.some(v => v.trim()));
  if (first === -1) return { headers: [], rows: [] };
  const headers = parsed[first];
  const rows = parsed.slice(first + 1)
    .filter(r => r.some(v => v.trim()))
    .map(r => {
      while (r.length < headers.length) r.push('');
      return r;
    });
  return { headers, rows };
}

// ── Monday column value formatting ──
const TYPE_LABELS = {
  text: 'Text', long_text: 'Long Text', email: 'Email', phone: 'Phone',
  status: 'Status', date: 'Date', numbers: 'Number', link: 'URL',
  dropdown: 'Dropdown', country: 'Country', checkbox: 'Checkbox',
  timeline: 'Timeline', week: 'Week', rating: 'Rating', hour: 'Hour'
};

function formatColValue(type, raw) {
  const v = String(raw).trim();
  if (!v) return null;

  switch (type) {
    case 'text':
    case 'long_text':
      return v;
    case 'email': {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return null;
      return `${v} ${v}`;
    }
    case 'phone': {
      const cleaned = v.replace(/[\s\-().+]/g, '');
      if (!/^\d{4,}$/.test(cleaned)) return null;
      return { phone: cleaned, countryShortName: 'US' };
    }
    case 'numbers': {
      const n = parseFloat(v);
      if (isNaN(n) || !isFinite(n)) return null;
      return String(n);
    }
    case 'date': {
      const d = new Date(v);
      if (isNaN(d.getTime())) return null;
      return { date: d.toISOString().split('T')[0] };
    }
    case 'link': {
      let url = v;
      if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
      if (!/^https?:\/\/.+\..+/i.test(url)) return null;
      return { url, text: v };
    }
    case 'checkbox': {
      const t = v.toLowerCase();
      const truthy = ['yes', 'true', '1', 'x', 'on', 'y'];
      return { checked: truthy.includes(t) ? 'true' : 'false' };
    }
    case 'status':
    case 'dropdown':
      return v;
    case 'country': {
      const upper = v.toUpperCase().trim();
      if (upper.length === 2) return { countryCode: upper, countryName: v };
      return { countryCode: '', countryName: v };
    }
    default:
      return v;
  }
}

function buildColVals(row, headers, mappings, cols) {
  const colVals = {};
  const warnings = [];
  mappings.forEach(m => {
    if (!m.csvCol || !m.colId) return;
    const ci = headers.indexOf(m.csvCol);
    if (ci < 0) return;
    const raw = (row[ci] || '').toString().trim();
    if (!raw) return;
    const col = cols.find(c => c.id === m.colId);
    const val = formatColValue(col?.type, raw);
    if (val === null) {
      warnings.push(`${m.csvCol}: invalid ${col?.type || 'unknown'} value "${raw}"`);
    } else {
      colVals[m.colId] = val;
    }
  });
  return { colVals, warnings };
}

// ── Steps config ──
const STEPS = [
  { num: 1, label: 'Paste CSV', icon: FiUpload },
  { num: 2, label: 'Connect Monday', icon: FiLink },
  { num: 3, label: 'Map Columns', icon: FiColumns },
  { num: 4, label: 'Sync', icon: FiZap },
];

export default function MondaySyncPage({ embedded = false }) {
  const dispatch = useDispatch();
  const { connected, boardName, groups, itemCols, subCols, loading: boardLoading, error: boardError } = useSelector(s => s.monday);
  const authToken = useSelector(s => s.auth.token);

  // Step state
  const [step, setStep] = useState(1);

  // Step 1 — CSV data
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const fileRef = useRef();

  // Step 2 — Connection
  const [apiKey, setApiKey] = useState('');
  const [boardId, setBoardId] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState('');

  // Step 3 — Mapping
  const [itemNameCol, setItemNameCol] = useState('');
  const [dupeMode, setDupeMode] = useState('reuse');
  const [subMode, setSubMode] = useState(false);
  const [subNameCol, setSubNameCol] = useState('');
  const [itemMappings, setItemMappings] = useState([{ csvCol: '', colId: '' }]);
  const [subMappings, setSubMappings] = useState([{ csvCol: '', colId: '' }]);
  const [mapTab, setMapTab] = useState('item');

  // Step 4 — Sync
  const [syncing, setSyncing] = useState(false);
  const [syncLogs, setSyncLogs] = useState([]);
  const [syncProgress, setSyncProgress] = useState({ done: 0, total: 0 });
  const [syncDone, setSyncDone] = useState(false);

  // ── CSV handlers ──
  const handlePaste = useCallback((e) => {
    const text = (e.clipboardData || window.clipboardData).getData('text');
    if (text && text.trim()) {
      const { headers: h, rows: r } = parseCSV(text);
      if (h.length) { setHeaders(h); setRows(r); }
    }
  }, []);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { headers: h, rows: r } = parseCSV(ev.target.result);
      if (h.length) { setHeaders(h); setRows(r); }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const updateCell = (ri, ci, val) => {
    setRows(prev => {
      const updated = [...prev];
      updated[ri] = [...updated[ri]];
      updated[ri][ci] = val;
      return updated;
    });
  };

  const deleteRow = (ri) => setRows(prev => prev.filter((_, i) => i !== ri));
  const addRow = () => setRows(prev => [...prev, new Array(headers.length).fill('')]);

  const clearData = () => { setHeaders([]); setRows([]); };

  const downloadCSV = () => {
    const esc = v => { v = String(v || ''); return v.includes(',') || v.includes('"') || v.includes('\n') ? '"' + v.replace(/"/g, '""') + '"' : v; };
    const lines = [headers.map(esc).join(','), ...rows.map(r => headers.map((_, i) => esc(r[i] || '')).join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `monday-sync-${Date.now()}.csv`; a.click();
  };

  // ── Connect ──
  const handleConnect = async () => {
    if (!boardId.trim()) return;
    try {
      await dispatch(connectBoard(boardId.trim(), apiKey.trim() || undefined));
    } catch (err) { /* error handled by redux */ }
  };

  // ── Auto-map ──
  const autoMap = () => {
    const cols = mapTab === 'item' ? itemCols : subCols;
    const maps = [];
    headers.forEach(h => {
      const low = h.toLowerCase().trim();
      const match = cols.find(c => c.title.toLowerCase().trim() === low)
        || cols.find(c => c.title.toLowerCase().includes(low) || low.includes(c.title.toLowerCase()));
      if (match) maps.push({ csvCol: h, colId: match.id });
    });
    if (!maps.length) maps.push({ csvCol: '', colId: '' });
    if (mapTab === 'item') setItemMappings(maps); else setSubMappings(maps);
  };

  // ── GQL helper for sync (direct call) ──
  const gql = async (query, variables) => {
    const hdrs = { 'Content-Type': 'application/json', 'Authorization': authToken };
    if (apiKey.trim()) hdrs['x-monday-token'] = apiKey.trim();
    const res = await fetch('/api/monday/graphql', {
      method: 'POST',
      headers: hdrs,
      body: JSON.stringify({ query, variables: variables || {} })
    });
    const j = await res.json();
    if (j.errors && j.errors.length) throw new Error(j.errors.map(e => e.message).join('; '));
    return j.data;
  };

  // ── Sync engine ──
  const log = (type, msg) => setSyncLogs(prev => [...prev, { type, msg, time: new Date().toLocaleTimeString() }]);

  const startSync = async () => {
    if (!selectedGroup || !rows.length) return;
    setSyncing(true); setSyncDone(false); setSyncLogs([]);
    const total = rows.length;
    setSyncProgress({ done: 0, total });

    const validItem = itemMappings.filter(m => m.csvCol && m.colId);
    const validSub = subMappings.filter(m => m.csvCol && m.colId);
    const nameIdx = itemNameCol ? headers.indexOf(itemNameCol) : 0;
    let ok = 0, fail = 0, done = 0;

    // Group rows by parent name
    const groupMap = new Map();
    rows.forEach((row, ri) => {
      const nm = (nameIdx >= 0 && row[nameIdx]) || ('Row ' + (ri + 1));
      if (!groupMap.has(nm)) groupMap.set(nm, []);
      groupMap.get(nm).push(ri);
    });

    log('info', `Grouped ${total} rows into ${groupMap.size} parent item(s).`);

    // Find existing items if reuse mode
    const existingIds = new Map();
    const boardIdVal = boardId.trim();
    if (dupeMode === 'reuse') {
      const names = [...groupMap.keys()];
      for (let i = 0; i < names.length; i += 50) {
        const chunk = names.slice(i, i + 50);
        try {
          const parts = chunk.map((nm, idx) =>
            `q${idx}: items_page_by_column_values(limit:3, board_id:${boardIdVal}, columns:[{column_id:"name",column_values:["${nm.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]}]) { items { id name } }`
          ).join('\n');
          const d = await gql('query {\n' + parts + '\n}');
          chunk.forEach((nm, idx) => {
            const res = d['q' + idx];
            if (res?.items?.length) existingIds.set(nm, res.items[0].id);
          });
        } catch (e) { /* search errors ok */ }
      }
      log('info', `Found ${existingIds.size} existing item(s) on the board.`);
    }

    // Create parent items
    const parentIds = new Map();
    for (const [nm] of groupMap) {
      if (existingIds.has(nm)) { parentIds.set(nm, existingIds.get(nm)); continue; }
      const rowIdxs = groupMap.get(nm);
      const firstRow = rows[rowIdxs[0]] || [];
      const { colVals, warnings } = buildColVals(firstRow, headers, validItem, itemCols);
      warnings.forEach(w => log('warn', `Item "${nm}": ${w}`));
      try {
        const q = `mutation($n:String!,$cv:JSON!){ create_item(board_id:${boardIdVal},group_id:"${selectedGroup}",item_name:$n,column_values:$cv){id name} }`;
        const d = await gql(q, { n: nm, cv: JSON.stringify(colVals) });
        parentIds.set(nm, d.create_item.id);
        log('success', `Created item "${nm}"`);
      } catch (err) {
        log('error', `Failed item "${nm}": ${err.message}`);
        rowIdxs.forEach(() => fail++);
        groupMap.delete(nm);
      }
    }

    // Create subitems or count items as done
    for (const [nm, rowIdxs] of groupMap) {
      const parentId = parentIds.get(nm);
      if (!parentId) { done += rowIdxs.length; setSyncProgress({ done, total }); continue; }

      if (!subMode) {
        ok += rowIdxs.length; done += rowIdxs.length;
        setSyncProgress({ done, total });
      } else {
        for (const ri of rowIdxs) {
          const row = rows[ri] || [];
          let subNm = '';
          if (subNameCol) { const si = headers.indexOf(subNameCol); if (si >= 0) subNm = row[si] || ''; }
          if (!subNm) subNm = row[0] || ('Row ' + (ri + 1));
          const { colVals, warnings } = buildColVals(row, headers, validSub, subCols);
          warnings.forEach(w => log('warn', `Row ${ri + 1}: ${w}`));
          try {
            const q = `mutation($n:String!,$cv:JSON!){ create_subitem(parent_item_id:${parentId},item_name:$n,column_values:$cv){id name} }`;
            await gql(q, { n: subNm, cv: JSON.stringify(colVals) });
            log('success', `Row ${ri + 1}: "${subNm}" → "${nm}"`);
            ok++;
          } catch (err) {
            log('error', `Row ${ri + 1}: Failed "${subNm}" — ${err.message}`);
            fail++;
          }
          done++;
          setSyncProgress({ done, total });
        }
      }
    }

    log(fail > 0 ? 'warn' : 'success', `Done. ${ok}/${total} synced.${fail ? ` ${fail} failed.` : ''}`);
    setSyncing(false);
    setSyncDone(true);
  };

  // ── Render helpers ──
  const activeMaps = mapTab === 'item' ? itemMappings : subMappings;
  const activeCols = mapTab === 'item' ? itemCols : subCols;
  const setActiveMaps = mapTab === 'item' ? setItemMappings : setSubMappings;

  const updateMapping = (idx, field, val) => {
    const setter = mapTab === 'item' ? setItemMappings : setSubMappings;
    setter(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: val };
      return updated;
    });
  };

  const removeMapping = (idx) => {
    const setter = mapTab === 'item' ? setItemMappings : setSubMappings;
    setter(prev => prev.filter((_, i) => i !== idx));
  };

  const addMapping = () => {
    const setter = mapTab === 'item' ? setItemMappings : setSubMappings;
    setter(prev => [...prev, { csvCol: '', colId: '' }]);
  };

  const pct = syncProgress.total ? Math.round((syncProgress.done / syncProgress.total) * 100) : 0;

  const content = (
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#054581]">Monday.com Sync</h1>
            <p className="text-sm text-gray-500 mt-1">Import CSV data into your Monday.com boards</p>
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex gap-1 mb-6 bg-white rounded-lg border p-1">
          {STEPS.map(s => (
            <button
              key={s.num}
              onClick={() => setStep(s.num)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-md text-sm font-medium transition-all ${
                step === s.num ? 'bg-[#1086a4] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                step === s.num ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-500'
              }`}>{s.num}</span>
              <span className="hidden sm:inline">{s.label}</span>
            </button>
          ))}
        </div>

        {/* ════════════ STEP 1: CSV ════════════ */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg border p-5">
              <h2 className="text-lg font-semibold mb-1">Paste Your Data</h2>
              <p className="text-sm text-gray-500 mb-4">Paste CSV data, upload a file, or edit cells directly.</p>

              {headers.length === 0 ? (
                <div>
                  {/* Paste zone */}
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-10 text-center cursor-pointer hover:border-[#1086a4] hover:bg-blue-50/30 transition-all"
                    onPaste={handlePaste}
                    tabIndex={0}
                  >
                    <FiUpload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-600">Click here and Ctrl+V / Cmd+V to paste CSV</p>
                    <p className="text-xs text-gray-400 mt-1">From Excel, Google Sheets, or any CSV source</p>
                  </div>

                  {/* File upload */}
                  <div className="mt-3">
                    <button
                      onClick={() => fileRef.current?.click()}
                      className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-[#1086a4] hover:bg-blue-50/30 transition-all"
                    >
                      <p className="text-sm text-gray-500">Or <span className="text-[#1086a4] underline">upload a CSV file</span></p>
                    </button>
                    <input ref={fileRef} type="file" accept=".csv,.tsv,.txt" onChange={handleFileUpload} className="hidden" />
                  </div>
                </div>
              ) : (
                <div>
                  {/* Toolbar */}
                  <div className="flex items-center justify-between mb-3 pb-3 border-b">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono text-gray-400 uppercase">Sheet</span>
                      <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">{rows.length} rows &middot; {headers.length} cols</span>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={addRow} className="text-xs px-2 py-1 border rounded hover:bg-gray-50 flex items-center gap-1">
                        <FiPlus className="w-3 h-3" /> Row
                      </button>
                      <button onClick={downloadCSV} className="text-xs px-2 py-1 border rounded hover:bg-gray-50 flex items-center gap-1">
                        <FiDownload className="w-3 h-3" /> CSV
                      </button>
                      <button onClick={clearData} className="text-xs px-2 py-1 border border-red-200 text-red-500 rounded hover:bg-red-50 flex items-center gap-1">
                        <FiTrash2 className="w-3 h-3" /> Clear
                      </button>
                    </div>
                  </div>

                  {/* Spreadsheet */}
                  <div className="overflow-auto max-h-[400px] border rounded-lg" onPaste={handlePaste}>
                    <table className="min-w-full text-sm border-collapse">
                      <thead>
                        <tr>
                          <th className="sticky top-0 bg-gray-100 border-b border-r px-2 py-1.5 text-center text-xs font-mono text-gray-400 w-10">#</th>
                          {headers.map((h, ci) => (
                            <th key={ci} className="sticky top-0 bg-gray-100 border-b border-r px-3 py-1.5 text-left text-xs font-medium text-[#1086a4] min-w-[120px]">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, ri) => (
                          <tr key={ri} className="group hover:bg-blue-50/30">
                            <td className="border-b border-r px-2 py-1 text-center text-xs font-mono text-gray-400 bg-gray-50">
                              <div className="flex items-center justify-center gap-1">
                                <span>{ri + 1}</span>
                                <button onClick={() => deleteRow(ri)} className="hidden group-hover:block text-red-400 hover:text-red-600">
                                  <FiX className="w-3 h-3" />
                                </button>
                              </div>
                            </td>
                            {headers.map((_, ci) => (
                              <td key={ci} className="border-b border-r px-1 py-0.5 min-w-[120px]">
                                <input
                                  className="w-full px-2 py-1 text-sm border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-[#1086a4] rounded"
                                  value={row[ci] || ''}
                                  onChange={e => updateCell(ri, ci, e.target.value)}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button onClick={() => setStep(2)} disabled={!headers.length} className="px-4 py-2 bg-[#1086a4] text-white rounded-lg text-sm font-medium disabled:opacity-40 hover:bg-[#0d7490] flex items-center gap-2">
                Next: Connect Monday <FiArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ════════════ STEP 2: Connect ════════════ */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg border p-5">
              <h2 className="text-lg font-semibold mb-1">Connect to Monday.com</h2>
              <p className="text-sm text-gray-500 mb-4">Enter your API key and Board ID. Leave API key blank to use the server default.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">API Key (optional override)</label>
                  <div className="flex gap-2">
                    <input
                      type={showKey ? 'text' : 'password'}
                      value={apiKey}
                      onChange={e => setApiKey(e.target.value)}
                      placeholder="Leave blank for server default"
                      className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-1 focus:ring-[#1086a4] focus:border-[#1086a4] outline-none"
                    />
                    <button onClick={() => setShowKey(!showKey)} className="px-3 py-2 border rounded-lg hover:bg-gray-50">
                      {showKey ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Board ID *</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={boardId}
                      onChange={e => setBoardId(e.target.value)}
                      placeholder="1234567890"
                      className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-1 focus:ring-[#1086a4] focus:border-[#1086a4] outline-none"
                    />
                    <button
                      onClick={handleConnect}
                      disabled={!boardId.trim() || boardLoading}
                      className="px-4 py-2 bg-[#1086a4] text-white rounded-lg text-sm font-medium disabled:opacity-40 hover:bg-[#0d7490]"
                    >
                      {boardLoading ? 'Connecting...' : 'Connect'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center gap-3">
                {connected ? (
                  <span className="inline-flex items-center gap-1.5 text-sm text-green-600 bg-green-50 border border-green-200 px-3 py-1 rounded-full">
                    <span className="w-2 h-2 rounded-full bg-green-500" /> Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-sm text-gray-400 bg-gray-50 border px-3 py-1 rounded-full">
                    <span className="w-2 h-2 rounded-full bg-gray-400" /> Not connected
                  </span>
                )}
              </div>

              {boardError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-start gap-2">
                  <FiAlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {boardError}
                </div>
              )}
            </div>

            {/* Board info */}
            {connected && (
              <div className="bg-white rounded-lg border p-5">
                <p className="text-sm font-mono text-[#1086a4] uppercase tracking-wider mb-3">Board Details</p>
                <p className="text-sm text-gray-700 mb-3">
                  <strong>{boardName}</strong> &middot; {groups.length} groups &middot; {itemCols.length} item cols &middot; {subCols.length} subitem cols
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Group</label>
                  <select
                    value={selectedGroup}
                    onChange={e => setSelectedGroup(e.target.value)}
                    className="w-full md:w-1/2 px-3 py-2 border rounded-lg text-sm focus:ring-1 focus:ring-[#1086a4] outline-none"
                  >
                    <option value="">— Select group —</option>
                    {groups.map(g => <option key={g.id} value={g.id}>{g.title}</option>)}
                  </select>
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50 flex items-center gap-2">
                <FiArrowLeft className="w-4 h-4" /> Back
              </button>
              <button onClick={() => setStep(3)} disabled={!connected || !selectedGroup} className="px-4 py-2 bg-[#1086a4] text-white rounded-lg text-sm font-medium disabled:opacity-40 hover:bg-[#0d7490] flex items-center gap-2">
                Next: Map Columns <FiArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ════════════ STEP 3: Map Columns ════════════ */}
        {step === 3 && (
          <div className="space-y-4">
            {/* Parent item config */}
            <div className="bg-white rounded-lg border p-5">
              <p className="text-sm font-mono text-[#1086a4] uppercase tracking-wider mb-3">Parent Item Setup</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Item Name Column</label>
                  <select value={itemNameCol} onChange={e => setItemNameCol(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#1086a4]">
                    <option value="">— Select —</option>
                    {headers.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">Rows with the same value share one parent item</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duplicate Handling</label>
                  <select value={dupeMode} onChange={e => setDupeMode(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#1086a4]">
                    <option value="reuse">Reuse existing item (match by name)</option>
                    <option value="create">Always create new</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Subitem toggle */}
            <div className="bg-white rounded-lg border p-5">
              <p className="text-sm font-mono text-[#1086a4] uppercase tracking-wider mb-3">Subitem Mode</p>
              <button onClick={() => setSubMode(!subMode)} className="flex items-center gap-3 w-full text-left">
                <div className={`w-10 h-5 rounded-full relative transition-colors ${subMode ? 'bg-[#1086a4]' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${subMode ? 'left-5' : 'left-0.5'}`} />
                </div>
                <div>
                  <p className="text-sm font-medium">Enable Subitems</p>
                  <p className="text-xs text-gray-400">Each CSV row becomes a subitem under its parent</p>
                </div>
              </button>
              {subMode && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subitem Name Column</label>
                  <select value={subNameCol} onChange={e => setSubNameCol(e.target.value)} className="w-full md:w-1/2 px-3 py-2 border rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#1086a4]">
                    <option value="">— Use first column —</option>
                    {headers.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              )}
            </div>

            {/* Column mapping */}
            <div className="bg-white rounded-lg border p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-mono text-[#1086a4] uppercase tracking-wider">Column Mapping</p>
                <div className="flex gap-2">
                  <button onClick={addMapping} className="text-xs px-2 py-1 border rounded hover:bg-gray-50 flex items-center gap-1">
                    <FiPlus className="w-3 h-3" /> Add Row
                  </button>
                  <button onClick={autoMap} className="text-xs px-2 py-1 border rounded hover:bg-gray-50 flex items-center gap-1">
                    <FiRefreshCw className="w-3 h-3" /> Auto-map
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-2 mb-4">
                <button onClick={() => setMapTab('item')} className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-all ${mapTab === 'item' ? 'bg-[#1086a4] text-white border-[#1086a4]' : 'text-gray-500 border-gray-200 hover:border-gray-300'}`}>
                  Item Columns
                </button>
                <button onClick={() => setMapTab('sub')} className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-all ${mapTab === 'sub' ? 'bg-[#1086a4] text-white border-[#1086a4]' : 'text-gray-500 border-gray-200 hover:border-gray-300'} ${!subMode ? 'opacity-40' : ''}`}>
                  Subitem Columns
                </button>
              </div>

              {/* Mapping header */}
              <div className="grid grid-cols-[1fr_30px_1fr_30px] gap-2 px-2 mb-2 text-xs text-gray-400 font-mono uppercase">
                <div>CSV Column</div><div /><div>Monday Column</div><div />
              </div>

              {/* Mapping rows */}
              {activeMaps.map((m, i) => (
                <div key={i} className="grid grid-cols-[1fr_30px_1fr_30px] gap-2 items-center mb-2">
                  <select value={m.csvCol} onChange={e => updateMapping(i, 'csvCol', e.target.value)} className="px-2 py-1.5 border rounded text-sm outline-none focus:ring-1 focus:ring-[#1086a4]">
                    <option value="">— CSV col —</option>
                    {headers.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <span className="text-center text-gray-400 text-xs">→</span>
                  <select value={m.colId} onChange={e => updateMapping(i, 'colId', e.target.value)} className="px-2 py-1.5 border rounded text-sm outline-none focus:ring-1 focus:ring-[#1086a4]">
                    <option value="">— Skip —</option>
                    {activeCols.map(c => <option key={c.id} value={c.id}>{c.title} ({TYPE_LABELS[c.type] || c.type})</option>)}
                  </select>
                  <button onClick={() => removeMapping(i)} className="text-red-400 hover:text-red-600 flex justify-center">
                    <FiX className="w-4 h-4" />
                  </button>
                </div>
              ))}

              {activeCols.length === 0 && (
                <p className="text-sm text-yellow-600 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  {mapTab === 'sub' ? 'No subitem columns found. Create at least one subitem on your board first.' : 'Connect to Monday.com first.'}
                </p>
              )}
            </div>

            <div className="flex justify-between">
              <button onClick={() => setStep(2)} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50 flex items-center gap-2">
                <FiArrowLeft className="w-4 h-4" /> Back
              </button>
              <button onClick={() => setStep(4)} className="px-4 py-2 bg-[#1086a4] text-white rounded-lg text-sm font-medium hover:bg-[#0d7490] flex items-center gap-2">
                Next: Review & Sync <FiArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ════════════ STEP 4: Sync ════════════ */}
        {step === 4 && (
          <div className="space-y-4">
            {/* Summary stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Rows', val: rows.length },
                { label: 'Item Maps', val: itemMappings.filter(m => m.csvCol && m.colId).length },
                { label: 'Sub Maps', val: subMode ? subMappings.filter(m => m.csvCol && m.colId).length : '-' },
                { label: 'Mode', val: subMode ? 'Subitems' : 'Items' }
              ].map((s, i) => (
                <div key={i} className="bg-white border rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-[#1086a4]">{s.val}</div>
                  <div className="text-xs text-gray-400 font-mono uppercase mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Sync details */}
            <div className="bg-white rounded-lg border p-5">
              <p className="text-sm font-mono text-[#1086a4] uppercase tracking-wider mb-3">Sync Details</p>
              <div className="grid grid-cols-[140px_1fr] gap-y-2 text-sm">
                <span className="text-gray-400">Board</span><span>{boardName}</span>
                <span className="text-gray-400">Group</span><span>{groups.find(g => g.id === selectedGroup)?.title || '(none)'}</span>
                <span className="text-gray-400">Mode</span><span>{subMode ? 'Parent items + Subitems' : 'Items only'}</span>
                <span className="text-gray-400">Item Name Col</span><span>{itemNameCol || '(first column)'}</span>
                <span className="text-gray-400">Duplicates</span><span>{dupeMode === 'reuse' ? 'Reuse existing' : 'Always create new'}</span>
              </div>
            </div>

            {/* Sync button */}
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={() => setStep(3)} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50 flex items-center gap-2">
                <FiArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                onClick={startSync}
                disabled={syncing || !rows.length || !selectedGroup}
                className="px-6 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold disabled:opacity-40 hover:bg-green-700 flex items-center gap-2"
              >
                <FiZap className="w-4 h-4" /> {syncing ? 'Syncing...' : `Sync ${rows.length} Row${rows.length === 1 ? '' : 's'} to Monday.com`}
              </button>
            </div>

            {/* Progress */}
            {(syncing || syncDone) && (
              <div className="bg-white rounded-lg border overflow-hidden">
                <div className="flex justify-between px-4 py-2 text-sm text-gray-500 font-mono">
                  <span>{syncProgress.done} / {syncProgress.total} rows</span>
                  <span>{pct}%</span>
                </div>
                <div className="h-1 bg-gray-100">
                  <div className="h-full bg-[#1086a4] transition-all" style={{ width: `${pct}%` }} />
                </div>

                {/* Log */}
                <div className="max-h-[300px] overflow-y-auto p-2">
                  {syncLogs.map((l, i) => (
                    <div key={i} className={`flex items-start gap-2 px-3 py-1 text-xs font-mono border-b border-gray-50 ${
                      l.type === 'success' ? 'text-green-600' : l.type === 'error' ? 'text-red-500' : l.type === 'warn' ? 'text-yellow-600' : 'text-gray-500'
                    }`}>
                      <span className="flex-shrink-0 mt-0.5">
                        {l.type === 'success' ? <FiCheck className="w-3 h-3" /> : l.type === 'error' ? <FiX className="w-3 h-3" /> : l.type === 'warn' ? <FiAlertTriangle className="w-3 h-3" /> : '•'}
                      </span>
                      <span className="break-all">{l.msg}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Post-sync actions */}
            {syncDone && (
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => { downloadCSV(); clearData(); setStep(1); }} className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-1">
                  <FiDownload className="w-4 h-4" /> Download CSV & Clear
                </button>
                <button onClick={() => setStep(1)} className="px-3 py-2 border rounded-lg text-sm hover:bg-gray-50">
                  Back to Sheet
                </button>
                <button onClick={() => { setSyncLogs([]); setSyncDone(false); setSyncProgress({ done: 0, total: 0 }); }} className="px-3 py-2 border border-red-200 text-red-500 rounded-lg text-sm hover:bg-red-50">
                  Clear Log
                </button>
              </div>
            )}
          </div>
        )}
      </div>
  );

  if (embedded) return content;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {content}
      </div>
    </div>
  );
}
